import React, { useState, useRef } from 'react';
import { User, Mail, Phone, MapPin, Calendar, Building2, Globe, MessageSquare, FileText, Download, Upload, Clock, Paperclip, MessageCircle, Folder } from 'lucide-react';

// Types
interface ContactDetails {
  id: string;
  name: string;
  initials: string;
  status: string;
  score: number;
  title: string;
  company: string;
  email: string;
  phone: string;
  website: string;
  location: string;
  addedDate: string;
  tags: string[];
  potentialDeal: number;
  owner: string;
  leadSource: string;
}

interface Document {
  id: string;
  name: string;
  uploadDate: string;
  size: string;
  uploadedBy: string;
  verified: boolean;
  fiscalYear: string;
  status: 'pending' | 'verified' | 'rejected';
}

interface Activity {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  user: string;
  duration?: string;
  messageCount?: number;
  attachmentCount?: number;
}

interface HistoryItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  action: 'UPLOAD' | 'SYNC';
  date: string;
  size: string;
  category: string;
}

// Mock Data
const mockContact: ContactDetails = {
  id: '1',
  name: 'John Doe',
  initials: 'JD',
  status: 'Active',
  score: 0,
  title: 'Chief Financial Officer',
  company: 'TechCorp Inc.',
  email: 'john.doe@techcorp.com',
  phone: '+1 (555) 123-4567',
  website: 'techcorp.com',
  location: 'San Francisco, CA',
  addedDate: '2024-01-15',
  tags: ['Enterprise', 'VIP', 'Quarterly Review'],
  potentialDeal: 0,
  owner: 'You',
  leadSource: 'Website'
};

const mockDocuments: Document[] = [
  {
    id: '1',
    name: 'annual_report_2024.pdf',
    uploadDate: '01/15/2024',
    size: '2.4 MB',
    uploadedBy: 'You',
    verified: true,
    fiscalYear: '2024-25',
    status: 'verified'
  },
  {
    id: '2',
    name: 'financial_statements.xlsx',
    uploadDate: '01/10/2024',
    size: '1.8 MB',
    uploadedBy: 'You',
    verified: false,
    fiscalYear: '2024-25',
    status: 'pending'
  }
];

const mockActivities: Activity[] = [
  {
    id: '1',
    type: 'email',
    title: 'Sent quarterly report',
    description: 'Shared the Q4 financial report and projections',
    timestamp: '2 hours ago',
    user: 'You',
    attachmentCount: 0
  },
  {
    id: '2',
    type: 'call',
    title: 'Product demo call',
    description: 'Discussed enterprise features and pricing',
    timestamp: '1 day ago',
    user: 'You',
    duration: '45 minutes'
  },
  {
    id: '3',
    type: 'whatsapp',
    title: 'Quick follow-up',
    description: 'Confirmed meeting for next week',
    timestamp: '2 days ago',
    user: 'You',
    messageCount: 0
  }
];

const mockHistoryItems: HistoryItem[] = [
  {
    id: '1',
    name: 'Screenshot (1034).png',
    type: 'file',
    action: 'UPLOAD',
    date: '11/21/2025, 10:45:28 PM',
    size: '109 KB',
    category: 'Income Tax Return 2025-26'
  },
  {
    id: '2',
    name: 'Screenshot (1035).png',
    type: 'file',
    action: 'UPLOAD',
    date: '11/21/2025, 10:40:29 PM',
    size: '150 KB',
    category: 'Income Tax Return 2025-26'
  },
  {
    id: '3',
    name: 'nigeria_contacts_50k.csv',
    type: 'file',
    action: 'UPLOAD',
    date: '11/21/2025, 9:55:47 PM',
    size: '2.8 MB',
    category: 'Income Tax Return 2025-26'
  }
];

// Authorization Popup Component
const AuthorizationPopup: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onAuthorize: (password: string, reason: string) => void;
  actionType: 'upload' | 'sync';
}> = ({ isOpen, onClose, onAuthorize, actionType }) => {
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.trim() && reason.trim()) {
      onAuthorize(password, reason);
      setPassword('');
      setReason('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Authorization Required
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your password"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason
              </label>
              <p className="text-sm text-gray-600 mb-2">
                Why {actionType === 'upload' ? 'upload' : 'sync'}?
              </p>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter reason for upload..."
                rows={3}
                required
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Continue
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// History Popup Component
const HistoryPopup: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  historyItems: HistoryItem[];
}> = ({ isOpen, onClose, historyItems }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">History</h2>
            <button
              onClick={onClose}
              className="text-[#7B7B7B] font-semibold hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Uploads</h3>
            <div className="space-y-4">
              {historyItems.map((item) => (
                <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${item.type === 'folder' ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                        {item.type === 'folder' ? (
                          <Folder className="w-5 h-5 text-blue-600" />
                        ) : (
                          <FileText className="w-5 h-5 text-gray-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900 break-words">
                            {item.name}
                          </h4>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                            {item.action}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {item.date}
                          </div>
                          <span>•</span>
                          <span>{item.size}</span>
                          <span>•</span>
                          <span className="text-blue-600 font-medium">{item.category}</span>
                        </div>
                      </div>
                    </div>
                    <button className="p-2 text-[#7B7B7B] font-semibold hover:text-gray-600 flex-shrink-0">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Syncs</h3>
            <div className="text-center py-8 text-gray-500">
              No sync history available
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contact Header Component
const ContactHeader: React.FC<{ contact: ContactDetails }> = ({ contact }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-semibold flex-shrink-0">
              {contact.initials}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">{contact.name}</h1>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  {contact.status}
                </span>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium flex items-center gap-1">
                  🔥 Score {contact.score}
                </span>
              </div>
              <div className="space-y-2 text-gray-600">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  <span className="font-medium">{contact.title}</span>
                  <span>at</span>
                  <span className="font-semibold text-gray-900">{contact.company}</span>
                </div>
                <div className="flex flex-wrap gap-4 text-sm">
                  <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-blue-600">
                    <Mail className="w-4 h-4" />
                    {contact.email}
                  </a>
                  <a href={`tel:${contact.phone}`} className="flex items-center gap-2 hover:text-blue-600">
                    <Phone className="w-4 h-4" />
                    {contact.phone}
                  </a>
                  <a href={`https://${contact.website}`} className="flex items-center gap-2 hover:text-blue-600" target="_blank" rel="noopener noreferrer">
                    <Globe className="w-4 h-4" />
                    {contact.website}
                  </a>
                </div>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {contact.location}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Added {contact.addedDate}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {contact.tags.map((tag, index) => (
                  <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm font-medium">
              <MessageSquare className="w-4 h-4" />
              Open Inbox
            </button>
            <button className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm font-medium">
              <FileText className="w-4 h-4" />
              View All Activities
            </button>
          </div>
        </div>

        <div className="lg:w-80 flex-shrink-0">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <div className="text-green-700 text-sm font-medium mb-1">Potential Deal</div>
            <div className="text-3xl font-bold text-green-700">
              ₦{contact.potentialDeal.toLocaleString()}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Account</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Owner</span>
                <span className="font-medium text-gray-900">{contact.owner}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Lead Source</span>
                <span className="font-medium text-gray-900">{contact.leadSource}</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-3">Quick Actions</div>
              <div className="grid grid-cols-2 gap-2">
                <button className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-white flex items-center justify-center gap-2 text-sm">
                  <Mail className="w-4 h-4" />
                  Email
                </button>
                <button className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-white flex items-center justify-center gap-2 text-sm">
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Documents Component
const Documents: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>(mockDocuments);
  const [selectedType, setSelectedType] = useState('Income Tax Return');
  const [selectedFY, setSelectedFY] = useState('All FYs');
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [showHistoryPopup, setShowHistoryPopup] = useState(false);
  const [authAction, setAuthAction] = useState<'upload' | 'sync'>('upload');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const documentTypes = [
    { name: 'Income Tax Return', count: 1 },
    { name: 'GST Returns', count: 1 },
    { name: 'PAN Card', count: 1 },
    { name: 'Aadhaar Card', count: 0 },
    { name: 'Bank Statements', count: 1 }
  ];

  const handleAuthSuccess = (password: string, reason: string) => {
    setShowAuthPopup(false);

    if (authAction === 'upload' && fileInputRef.current) {
      fileInputRef.current.click();
    } else if (authAction === 'sync' && folderInputRef.current) {
      folderInputRef.current.click();
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach(file => {
        const newDoc: Document = {
          id: Date.now().toString() + Math.random(),
          name: file.name,
          uploadDate: new Date().toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          }),
          size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
          uploadedBy: 'Current User',
          verified: false,
          status: 'pending',
          fiscalYear: '2024-25'
        };
        setDocuments(prev => [...prev, newDoc]);
      });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFolderUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach(file => {
        const newDoc: Document = {
          id: Date.now().toString() + Math.random(),
          name: `[Folder] ${file.name}`,
          uploadDate: new Date().toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          }),
          size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
          uploadedBy: 'Current User',
          verified: false,
          status: 'pending',
          fiscalYear: '2024-25'
        };
        setDocuments(prev => [...prev, newDoc]);
      });
    }
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  const triggerUpload = (action: 'upload' | 'sync') => {
    setAuthAction(action);
    setShowAuthPopup(true);
  };

  const getStatusBadge = (status: string, verified: boolean) => {
    if (verified) {
      return (
        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
          Verified
        </span>
      );
    }

    switch (status) {
      case 'pending':
        return (
          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
            Pending
          </span>
        );
      case 'rejected':
        return (
          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2 mb-6">
          <FileText className="w-5 h-5 text-gray-700" />
          <h2 className="text-xl font-bold text-gray-900">Documents</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Document Types</h3>
            <div className="space-y-2">
              {documentTypes.map((type) => (
                <button
                  key={type.name}
                  onClick={() => setSelectedType(type.name)}
                  className={`w-full px-4 py-3 rounded-lg text-left flex items-center justify-between transition-colors ${selectedType === type.name
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  <span className="text-sm font-medium">{type.name}</span>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${selectedType === type.name ? 'bg-white text-gray-900' : 'bg-gray-200 text-gray-600'
                    }`}>
                    {type.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-semibold text-gray-900">{selectedType}</h3>
                <span className="text-sm text-gray-500">Last sync: Never</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <select
                  value={selectedFY}
                  onChange={(e) => setSelectedFY(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option>All FYs</option>
                  <option>2024-25</option>
                  <option>2023-24</option>
                  <option>2022-23</option>
                </select>

                <button
                  onClick={() => triggerUpload('sync')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium flex items-center gap-2"
                >
                  <Folder className="w-4 h-4" />
                  Sync from local
                </button>

                <button
                  onClick={() => triggerUpload('upload')}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Upload document
                </button>

                <button
                  onClick={() => setShowHistoryPopup(true)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium flex items-center gap-2"
                >
                  <Clock className="w-4 h-4" />
                  History
                </button>

                {/* Hidden file input for upload */}
                <input
                  type="file"
                  multiple
                  accept="*/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  ref={fileInputRef}
                />

                <input
                  type="file"
                  multiple
                  {...{
                    webkitdirectory: "",
                    directory: ""
                  } as any}
                  onChange={handleFolderUpload}
                  className="hidden"
                  ref={folderInputRef}
                />
              </div>
            </div>

            <div className="space-y-4">
              {documents.map((doc) => (
                <div key={doc.id} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 mb-1 break-words">{doc.name}</h4>
                        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                          <span>Uploaded {doc.uploadDate}</span>
                          <span>•</span>
                          <span>{doc.size}</span>
                          <span>•</span>
                          <span>by {doc.uploadedBy}</span>
                          {getStatusBadge(doc.status, doc.verified)}
                        </div>
                        <div className="mt-1 text-sm text-gray-500">{doc.fiscalYear}</div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium">
                        Replace
                      </button>
                      <button className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium flex items-center gap-2">
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <AuthorizationPopup
        isOpen={showAuthPopup}
        onClose={() => setShowAuthPopup(false)}
        onAuthorize={handleAuthSuccess}
        actionType={authAction}
      />

      <HistoryPopup
        isOpen={showHistoryPopup}
        onClose={() => setShowHistoryPopup(false)}
        historyItems={mockHistoryItems}
      />
    </>
  );
};

// Activity Timeline Component
const ActivityTimeline: React.FC = () => {
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <Mail className="w-5 h-5" />;
      case 'whatsapp':
        return <MessageCircle className="w-5 h-5" />;
      case 'call':
        return <Phone className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'email':
        return 'bg-purple-100 text-purple-600';
      case 'whatsapp':
        return 'bg-green-100 text-green-600';
      case 'call':
        return 'bg-orange-100 text-orange-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getActivityBadge = (type: string) => {
    switch (type) {
      case 'email':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'whatsapp':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'call':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900">Activity Timeline</h2>
        <span className="text-sm text-gray-500">{mockActivities.length} activities</span>
      </div>

      <div className="space-y-6">
        {mockActivities.map((activity, index) => (
          <div key={activity.id} className="relative">
            {index !== mockActivities.length - 1 && (
              <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gray-200" />
            )}
            <div className="flex gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${getActivityColor(activity.type)}`}>
                {getActivityIcon(activity.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">{activity.title}</h3>
                    <p className="text-gray-600 text-sm">{activity.description}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getActivityBadge(activity.type)}`}>
                      {activity.type}
                    </span>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      {activity.timestamp}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {activity.user}
                  </div>
                  {activity.duration && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {activity.duration}
                    </div>
                  )}
                  {activity.messageCount && (
                    <div className="flex items-center gap-1">
                      <MessageCircle className="w-4 h-4" />
                      {activity.messageCount} messages
                    </div>
                  )}
                  {activity.attachmentCount && (
                    <div className="flex items-center gap-1">
                      <Paperclip className="w-4 h-4" />
                      {activity.attachmentCount} attachments
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main App Component
const ContactDetail: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <ContactHeader contact={mockContact} />
        <Documents />
        <ActivityTimeline />
      </div>
    </div>
  );
};

export default ContactDetail;